import {Component, OnInit, OnDestroy, HostListener} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Sidenavaction} from '../../../../../../assets/js/application/sidenavaction';
import {PersonRoleService} from '../../../../../services/personRole.service';
import PersonRoleModel from '../../../../../services/personRole.model';
import * as moment from 'moment';
import BusinessTypeModel from 'src/app/services/businessType.model';
import {BusinessTypeService} from 'src/app/services/businessType.service';
import {ToastrService} from 'ngx-toastr';
import {CommonMethodsService} from 'src/app/services/commonMethods.service';
import {LocationStrategy} from '@angular/common';
import {PreviousRouteService} from 'src/app/services/previousRouteService';
import {BatchLoader} from "../../../../../Utils/batch-loader.util";

declare var $: any;

/**
 * @fileOverview PersonRoleComponent
 * @module AdminPanel/UserAccess/person-role
 * @description Manages the creation, update, and listing of person roles with batch-loaded pagination.
 * Provides UI for administrators to associate business types with person roles, validate form data,
 * and search/filter roles.
 */

/**
 * @component PersonRoleComponent
 * @selector app-person-role
 * @templateUrl ./person-role.component.html
 * @styleUrls ['./person-role.component.scss']
 * @implements {OnInit, OnDestroy}
 * @description Reactive form component for person role management (CRUD operations).
 * 
 * **Core Responsibilities:**
 * 1. Display batch-loaded list of person roles (12 items per batch) with infinite scroll
 * 2. Provide reactive form for creating new person roles
 * 3. Enable edit mode to update existing person roles
 * 4. Search and filter roles by name and code with debouncing (300ms)
 * 5. Associate business types with roles using multi-select form control
 * 6. Format timestamps for display (DD-MMM-YYYY hh:mm:ss A)
 * 7. Handle form validation with specific rules per field
 * 8. Prevent double-click submissions
 * 9. Clean up resources on component destruction
 * 
 * **Data Flow:**
 * - Fetch all person roles and business types on component init
 * - Initialize batch loader with 12-item batches
 * - Load first batch and display in template
 * - On scroll near bottom, automatically load next batch
 * - On search, filter roles and reset pagination
 * 
 * **Form Fields:**
 * - `name` (string): 3-20 chars, letters and spaces only, required
 * - `code` (string): 3-10 chars, alphanumeric only, optional
 * - `businessType` (BusinessTypeModel[]): Multi-select, required
 * - `description` (string): 0-200 chars, optional
 * 
 * **Features:**
 * - Create mode: Submit new role with empty form
 * - Edit mode: Update existing role from list
 * - Batch loader: 12 items per batch, infinite scroll
 * - Search: Debounced (300ms) by name or code
 * - Timestamps: Formatted in DD-MMM-YYYY hh:mm:ss A format
 * - Double-click prevention: Disables buttons during submission
 * 
 * **Dependencies:**
 * - PersonRoleService: CRUD operations for person roles
 * - BusinessTypeService: Fetch business type master data
 * - ToastrService: Toast notifications for user feedback
 * - CommonMethodsService: Shared utility methods
 * - BatchLoader: Pagination utility for infinite scroll
 */
@Component({
  selector: 'app-person-role',
  templateUrl: './person-role.component.html',
  styleUrls: ['./person-role.component.scss']
})
export class PersonRoleComponent implements OnInit, OnDestroy {
  /** @property personRoles @type {PersonRoleModel[]} @description Complete list of all person roles fetched from service @public */
  public personRoles: PersonRoleModel[] = [];
  
  /** @property personRoleList @type {PersonRoleModel[]} @description Currently displayed batch of person roles from batch loader @public */
  public personRoleList: PersonRoleModel[] = [];
  
  /** @property personRole @type {PersonRoleModel} @description Current person role object for form binding (create/edit mode) @public */
  public personRole: PersonRoleModel = new PersonRoleModel();
  
  /** @property filteredPersonRoles @type {PersonRoleModel[]} @description Filtered list of person roles from search query @public */
  public filteredPersonRoles: PersonRoleModel[] = [];
  
  /** @property businessTypes @type {Array<BusinessTypeModel>} @description Master data of all available business types for dropdown @public */
  public businessTypes: Array<BusinessTypeModel>;
  
  /** @property businessType @type {BusinessTypeModel} @description Single business type reference @public */
  public businessType: BusinessTypeModel;
  
  /** @property search @type {string} @description Search query string for filtering roles @public */
  public search: string;
  
  /** @property submitButton @type {boolean} @description Flag to determine if form is in create mode (submit button visible) @public */
  public submitButton: boolean;
  
  /** @property updateButton @type {boolean} @description Flag to determine if form is in edit mode (update button visible) @public */
  public updateButton: boolean;
  
  /** @property registerForm @type {FormGroup} @description Reactive form group for person role creation/update with validation rules @public */
  public registerForm: FormGroup;
  
  /** @property submitted @type {boolean} @description Flag to track if form has been submitted (for validation error display) @public */
  public submitted = false;
  
  /** @property personRoleDoubleClick @type {boolean} @description Flag to prevent double-click submission on form @public */
  public personRoleDoubleClick: boolean;
  
  /** @property NoDataMessage @type {boolean} @description Flag to display "no data" message when list is empty @public */
  public NoDataMessage: boolean = false;
  
  /** @property clearSearchInput @type {boolean} @description Flag to clear search input field from template @public */
  public clearSearchInput: boolean = false;
  
  /** @property listLoader @type {boolean} @description Loading spinner flag for batch loader (shows during pagination load) @public */
  public listLoader = false;
  
  /** @property startIndex @type {number} @description Tracks how many items have been loaded in pagination @public */
  public startIndex = 0;
  
  /** @property isFiltering @type {boolean} @description Flag indicating if search filter is active @public */
  public isFiltering: boolean = false;
  
  /** @property noList @type {boolean} @description Flag indicating if current list is empty @public */
  public noList: boolean = false;
  
  /** @property enableDisablePageLoader @type {boolean} @description Page-level loader flag for initial data load @public */
  enableDisablePageLoader: boolean;
  
  /** @property searchTimeout @type {any} @description Timeout ID for debounced search (300ms) @private */
  searchTimeout: any;
  
  /** @property formFieldCount @type {number} @description Total number of form fields (used for layout calculation) @public */
  public formFieldCount: number = 0;
  
  /** @property showForm @type {boolean} @description Flag to toggle form visibility on UI @public */
  public showForm: boolean = false;

  /**
   * @constructor
   * @param {ToastrService} toasterService - Toast notification service for user feedback
   * @param {Sidenavaction} sidenavaction - Navigation menu controller service
   * @param {FormBuilder} formBuilder - Angular reactive form builder
   * @param {PersonRoleService} personRoleService - Person role CRUD service
   * @param {BusinessTypeService} businessTypeService - Business type master data service
   * @param {CommonMethodsService} commonMethodsService - Shared utility methods service
   * @param {LocationStrategy} locationStrategy - Location tracking for breadcrumb
   * @param {PreviousRouteService} previousRouteService - Previous route tracking service
   * @public
   */
  constructor(private toasterService: ToastrService, public sidenavaction: Sidenavaction, public formBuilder: FormBuilder, public personRoleService: PersonRoleService, public businessTypeService: BusinessTypeService, public commonMethodsService: CommonMethodsService,
              private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.personRole = new PersonRoleModel();
    /*form validation*/
    this.registerForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(20), Validators.pattern('^[a-zA-Z ]*$')]],
      code: ['', [Validators.minLength(3), Validators.maxLength(10), Validators.pattern('^[A-Za-z0-9]*$')]],
      businessType: [[], Validators.required],
      description: ['', [Validators.maxLength(200)]]
    });
    // Calculate field count after form is built // new
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  /** @property batchLoader @type {BatchLoader<any>} @description Pagination utility for batch-loaded infinite scroll @private */
  private batchLoader: BatchLoader<any>;

  /**
   * @method ngOnInit
   * @async
   * @description Angular lifecycle hook. Initializes component on load with:
   * 1. Scroll page to top
   * 2. Reset all component state and lists
   * 3. Fetch business types master data
   * 4. Fetch all person roles from service
   * 5. Initialize batch loader and load first batch
   * 
   * @public
   * @async
   * @returns {Promise<void>}
   */
  public async ngOnInit() {
    try {
      window.scrollTo(0, 0);
      this.enableDisablePageLoader = true;
      this.clear();
      this.personRoles = [];
      this.personRoleList = [];
      this.filteredPersonRoles = [];
      this.isFiltering = false;
      this.search = '';
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      this.sidenavaction.x_navigation();
      this.businessTypes = await this.businessTypeService.getAllBusinessTypes();
      await this.getPersonRoles();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.enableDisablePageLoader = false;
    }
  }

  /**
   * @method onScroll
   * @description Host listener for window scroll events. Detects when user scrolls near
   * the bottom and triggers loading of next batch (50ms debounce).
   * 
   * @public
   * @hostListener window:scroll
   * @returns {void}
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /** @property scrollTimeout @type {any} @description Timeout ID for debounced scroll handler (50ms) @private */
  private scrollTimeout: any;

  /**
   * @method checkScrollAndLoad
   * @description Checks if user scrolled near bottom and loads next batch with 50ms debounce.
   * Triggers batch loader to fetch next batch if not already loading.
   * 
   * @private
   * @returns {void}
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * @method initializeBatchLoader
   * @description Initializes batch loader for pagination (12 items per batch).
   * Handles both full list and filtered list scenarios based on isFiltering flag.
   * 
   * @private
   * @returns {void}
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredPersonRoles : this.personRoles,
      (updatedList) => {
        this.personRoleList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * @method ngOnDestroy
   * @description Angular lifecycle hook. Cancels any ongoing HTTP requests from both services,
   * marks batch loader as destroyed, and resets batch loader state to prevent memory leaks.
   * 
   * @public
   * @returns {void}
   */
  public ngOnDestroy(): void {
    this.personRoleService.cancelOnGoingHttpRequests();
    this.businessTypeService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader.reset();
  }

  /**
   * @property personRoleFormValidation
   * @getter
   * @description Getter for convenient access to form controls in template.
   * Used in template with form validation error display.
   * 
   * @public
   * @type {AbstractControl}
   * @returns {any} Form controls object
   */
  // species type register form convenience getter for easy access to form fields
  public get personRoleFormValidation() {
    return this.registerForm.controls;
  }

  /**
   * @method getPersonRoles
   * @async
   * @description Fetches all person roles from service, formats timestamps, initializes batch loader,
   * and loads first batch for display. Handles errors gracefully.
   * 
   * @public
   * @async
   * @returns {Promise<void>}
   */
  public async getPersonRoles() {
    try {
      this.personRoles = await this.personRoleService.getAllPersonRoles();
      if (this.personRoles.length > 0) {
        this.personRoles.forEach(eachPersonRole => {
          eachPersonRole.updatedAt = JSON.parse(JSON.stringify(moment(eachPersonRole.updatedAt).format('DD-MMM-YYYY hh:mm:ss A')));
        });
      }
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader?.reset();     // Clear state
      await this.batchLoader?.loadNextBatch(true); // Load first batch
      this.enableDisablePageLoader = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.enableDisablePageLoader = false;
    }
  }

  /**
   * @method applySearch
   * @async
   * @description Applies search filter to person roles by name and code.
   * Resets pagination and loads filtered results, debounced at 300ms.
   * Handles both filtered and unfiltered scenarios.
   * 
   * @public
   * @async
   * @returns {Promise<void>}
   */
  // search functionality
  public async applySearch(): Promise<void> {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.personRoles.filter(role =>
        role.name?.toLowerCase().includes(query) ||
        role.code?.toLowerCase().includes(query)
      )
      : this.personRoles;
    this.filteredPersonRoles = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      //  Force noList = true if nothing to show
      this.noList = true;
      this.personRoleList = [];
      return;
    }
    // optional: handle scroll if user already at bottom
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  /**
   * @method onSearchChanged
   * @description Handles search input change with 300ms debounce.
   * Called from template on search field input to apply filtering.
   * 
   * @public
   * @returns {void}
   */
  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch(); // debounced
    }, 300);
  }

  /**
   * @method addPersonRole
   * @async
   * @description Creates new person role or updates existing role. Validates form before submission,
   * maps selected business type IDs to full objects, prevents double-click, and reloads list.
   * 
   * **Create Mode:** Submits new role with selected business types.
   * **Edit Mode:** Updates existing role without submitting ID, clones object for safety.
   * 
   * @public
   * @async
   * @returns {Promise<void>}
   */
  /*Add speciesType*/
  public async addPersonRole() {
    this.submitted = true;
    // stop here if form is invalid
    if (this.registerForm.invalid) {
      return;
    }
    // GET FORM VALUES
    const formData = this.registerForm.value; // contains updated values
    // Convert selected IDs to full object array with businessTypeID and name
    const selectedBusinessTypes = this.businessTypes
      .filter(bt => formData.businessType.includes(bt.id))
      .map(bt => ({
        businessTypeID: bt.id,
        name: bt.name
      }));
    const payload = { ...formData, businessType: selectedBusinessTypes};
    try {
      this.personRoleDoubleClick = true;
      if (this.submitButton) {
        await this.personRoleService.create(payload);
        this.toasterService.success('Person role created successfully', '', {
          timeOut: 2000
        });
      } else {
        const updatePersonRole = {...payload}
        delete updatePersonRole.id;
        await this.personRoleService.updateOneByID(this.personRole.id, updatePersonRole);
        this.toasterService.success('Person role updated successfully', '', {
          timeOut: 2000
        });
      }
      await this.ngOnInit();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.personRoleDoubleClick = false;
    }
  }

  /**
   * @method getPersonRoleById
   * @description Retrieves person role by ID from current list and populates reactive form with data.
   * Used for edit mode to pre-fill form with existing role values and associated business types.
   * 
   * @public
   * @param {string} id - The unique identifier of person role to retrieve
   * @returns {PersonRoleModel} The found person role or new empty PersonRoleModel instance
   */
  /*Get species type by id*/
  public getPersonRoleById(id: string) {
    this.showForm = true;
    this.personRoleDoubleClick = false;
    this.updateButton = true;
    this.submitButton = false;
    const foundPersonRole = this.personRoleList.find(role => role?.id === id);
    this.personRole = foundPersonRole ? {...foundPersonRole} : new PersonRoleModel(); // clone object
    if (this.personRole) {
      const businessTypeIds = this.personRole.businessType?.map(bt => bt.businessTypeID) || [];
      this.registerForm.patchValue({
        name: this.personRole.name,
        businessType: businessTypeIds,
        code: this.personRole.code,
        description: this.personRole.description
      });
    }
    return this.personRole;
  }

  /**
   * @method clear
   * @description Resets form and all form-related state to defaults.
   * Called on Cancel button or for clearing previous form data before new operation.
   * 
   * @public
   * @returns {void}
   */
  public clear() {
    this.registerForm.reset();
    this.personRole = new PersonRoleModel();
    this.personRoleDoubleClick = false;
    this.submitButton = false;
    this.updateButton = false;
    this.submitted = false;
    this.showForm = false;
  }

  /**
   * @method openForm
   * @description Initializes create mode. Sets form state flags to show submit button,
   * displays form for new person role creation.
   * 
   * @public
   * @returns {void}
   */
  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    // Count form fields to toggle layout
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }
}
